import { HStack, Modal, VStack } from '@musinsa/mwms-ds'
import { Icon as LayerIcon } from '@musinsa/pbo-assets'
import Text from '@/components/content/Text'
import { colors, colorSystem } from '@/styles/theme'
import { tokens } from '@musinsa/layer-tokens/desktop'
import styled from 'styled-components'
import Input from '@/components/content/Input'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useSetCapacityDay, useSuspenseGetCapacityDay } from '@/services/capacity/useCapacityQueries'
import { CapacityDayFormSchema, capacityDayFormSchema } from '../../schemas/capacityDayFormSchema'
import dayjs from 'dayjs'
import { SetCapacityDayReq } from '@/services/capacity/types'
import MToast from '@/utils/modal/MToast'
import { DATE_FORMAT } from '@/utils/consts'
import MAlert from '@/utils/modal/MAlert'
import { Radio } from '@/components/content/Radio'
import { useCutOffDate } from '../../hooks/useCutOffDate'
import { MENU_CODE } from '../../constants'

interface Props {
  warehouseCode: string
  onClose: () => void
}

export function SetCapacityModal({ warehouseCode, onClose }: Props) {
  const { cutOffDate } = useCutOffDate()

  // 익일 기준 케파를 변경하므로 익일 기준 조회
  const nextCutOffDate = dayjs(cutOffDate).add(1, 'day').format(DATE_FORMAT.date)

  const { data: b2cCapaData } = useSuspenseGetCapacityDay({ date: nextCutOffDate, warehouseCode, capacityType: 'B2C' }, MENU_CODE)

  const warehouseName = b2cCapaData?.warehouseName
  const isUnlimited = b2cCapaData?.isUnlimited

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<CapacityDayFormSchema>({
    resolver: zodResolver(capacityDayFormSchema),
    defaultValues: {
      b2cBaseCapacity: b2cCapaData?.baseCapacity,
      isUnlimited,
    },
    mode: 'onChange',
  })

  const { mutateAsync: setCapacityDayMutateAsync } = useSetCapacityDay(MENU_CODE)

  const handleFormSubmit = async (form: CapacityDayFormSchema) => {
    // 익일 기준 케파를 변경하는 것
    const payload: SetCapacityDayReq = {
      warehouseCode,
      date: nextCutOffDate,
      isUnlimited: form.isUnlimited,
      capacityType: 'B2C',
      baseCapacity: form.b2cBaseCapacity,
    }

    try {
      await setCapacityDayMutateAsync(payload)
      MToast.show('기준케파가 저장되었습니다.\n변경된 내용은 익일 적용됩니다.')
      onClose()
    } catch {
      MAlert.show('B2C 케파 저장에 실패했습니다.')
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <HStack gap={8} align="center">
          <Text kind="Headline_20_bold">기준 케파 관리</Text>
          <Text kind="Headline_16_regular">{warehouseName}</Text>
        </HStack>
      }
      buttons={[
        { kind: 'secondary', content: '취소', onClick: onClose },
        { kind: 'primary_light', content: '저장', onClick: handleSubmit(handleFormSubmit), disabled: !isValid },
      ]}
    >
      <DescriptionBox gap={8} align="flex-start">
        <IconWrap>
          <LayerIcon name="ic-infoCircle" size={18} filled color={colors.gray500} />
        </IconWrap>
        <Text kind="Body_14_regular" color={tokens.fg} style={{ fontFamily: "'Pretendard', sans-serif" }}>
          변경 시, 설정한 내용은 익일부터 적용됩니다.
        </Text>
      </DescriptionBox>

      <HStack gap={16} align="flex-start" style={{ marginTop: 24 }}>
        <Label kind="Body_14_medium">케파 제한 여부</Label>
        <Controller
          control={control}
          name="isUnlimited"
          render={({ field: { value, onChange } }) => (
            <VStack gap={4}>
              <HStack gap={32}>
                <Radio value="true" checked={value} label="무한출고" onChange={() => onChange(true)} />
                <Radio value="false" checked={!value} label="제한출고" onChange={() => onChange(false)} />
              </HStack>
              {value ? (
                <Text kind="Body_12_regular" color={colorSystem.text.common.tertiary}>
                  기준 케파와 관계없이 출고 할당이 계속 내려갑니다.
                </Text>
              ) : (
                <Text kind="Body_12_regular" color={colorSystem.text.common.tertiary}>
                  기준 케파 도달 시, 출고가 제한됩니다.
                </Text>
              )}
            </VStack>
          )}
        />
      </HStack>

      <HStack gap={16} align="flex-start" style={{ marginTop: 16 }}>
        <Label kind="Body_14_medium">B2C 케파</Label>
        <Controller
          control={control}
          name="b2cBaseCapacity"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <div style={{ width: '100%' }}>
              <Input
                type="number"
                block
                size="sm"
                placeholder="기준케파를 입력해 주세요."
                value={value?.toLocaleString()}
                onChange={(v) => {
                  onChange(v === '' ? '' : Number(v))
                }}
              />
              {error && (
                <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                  {error.message}
                </ErrorMessage>
              )}
            </div>
          )}
        />
      </HStack>
    </Modal>
  )
}

const Label = styled(Text)`
  min-width: 110px;
  padding: 4px 0 4px;

  &::after {
    content: '*';
    color: ${colorSystem.icon.error};
    position: relative;
    left: 2px;
    top: 3px;
  }
`

const ErrorMessage = styled(Text)`
  margin-top: 2px;
  margin-left: 4px;
`

const DescriptionBox = styled(HStack)`
  padding: 16px 12px;
  border: 1px solid #ebebeb;
  border-radius: 4px;
  background-color: rgba(224, 224, 224, 0.2);
`

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  height: 20px;
  flex-shrink: 0;
`
